import React, { useEffect, useState } from "react";
import useForm from "../../hooks/useForm";
import { ClipLoader } from "react-spinners";
import LoginInput from "../../components/Form/LoginInput";
import LoginButton from "../../components/Form/LoginButton";
import facebookIcon from "../../assets/Icons/facebook.svg";
import googleIcon from "../../assets/Icons/google.svg";
import appleIcon from "../../assets/Icons/apple.svg";
import blueBg from "../../assets/Login/bluebg.svg";
import loginImage from "../../assets/Login/image1.png";
import LoginImageMobile from "../../assets/Login/mobilebg.svg";
import LoginImageTab from "../../assets/Login/bluebg-tabs.png";
import { useThunk } from "../../hooks/useThunk";
import { forgetPassword } from "../../store";
import { USER_INPUT_FIELD_TYPES } from "../../constants/userInputFieldNames";
import showToast from "../../utils/toastNotifications";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { getGoogleAuthUrl } from "../../utils/googleAuth";

const ForgotPassword = () => {

  const [doForgetPassword, isLoading, forgetPasswordError] = useThunk(forgetPassword)
  const [inlineMessage, setInlineMessage] = useState(null);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
  } = useForm({
      email: "",
  }, {email: true}, {email: USER_INPUT_FIELD_TYPES.EMAIL}); 

  useEffect(() => {
    if (!inlineMessage) return;

    const timeoutId = setTimeout(() => {
      setInlineMessage(null);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [inlineMessage]);

  const getForgotPasswordErrorMessage = (message = "") => {
    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage.includes("user") && normalizedMessage.includes("not found")) {
      return "We could not find an account with that email address.";
    }

    return message || "Unable to send a password reset link. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      email: true,
    });

    if (validateForm()) {
      const result = await doForgetPassword(formData);

      if (result.success) {
        const successMessage = result.response?.message ||
          "Please check your email for instructions to reset your password.";
        setInlineMessage({ type: "success", text: successMessage });
        showToast(
          "success",
          successMessage,
          {
            autoClose: 10000,
            closeOnClick: true,
            pauseOnHover: true,
          }
        );
      } else {
        const errorMessage = getForgotPasswordErrorMessage(result.error?.message);
        setInlineMessage({ type: "error", text: errorMessage });
        showToast("error", errorMessage, {
          autoClose: 10000,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    }
  };

  // redirect user to google concent screen
  const handleGoogleSignin = (e) => {
    e.preventDefault();
    const authUrl = getGoogleAuthUrl();
    if(!authUrl){
      showToast("error", errorMessages.FAILED_GOOGLE_SIGNIN)
      return
    }
    window.location.href = authUrl
  }

  return (
    <div className="flex flex-col relative w-full overflow-x-hidden h-screen">
      {/* WatermarkBg*/}
      <div className="hidden">
        <img
          src={LoginImageMobile}
          alt="background"
          className="hidden"
        />
        <img
          src={LoginImageTab}
          alt="background"
          className="hidden"
        />
      </div>

      <div className="absolute z-0 top-0 right-0 w-full login-br:block">
        <img
          src={blueBg}
          alt="background"
          className="hidden lg:block absolute -right-15 -top-20 lg:right-0 w-full  lg:max-w-[650px] xl:max-w-[700px] 2xl:max-w-[750px] 1920-br:max-w-[850px] 2560-br:max-w-[1400px] transition-all duration-300"
        />
      </div>

      {/* Form Container-left  */}
      <div className="relative z-10 mx-auto mt-20 mb-0 sm:mt-16 pb-10 flex flex-grow items-start lg:items-center justify-center login-br:justify-between w-full 2xl:max-w-[1536px] auth-layout-structure-in-tab-screen-higher-than-880-height">
        <div className="w-full lg:w-[90%] 2xl:w-[60%] flex flex-col justify-center px-10 lg:px-20 ml-0 1920-br:translate-x-[80px]">
          <h1 className="w-[40%] md:w-full text-level-1 font-roboto font-bold leading-[44px] text-primary mb-2 sm:mb-4">
            Forgot Password?
          </h1>
          <p className="mb-0 sm:mb-10 font-inter text-[18px] text-gray max-w-[280px] md:max-w-[320px] lg:max-w-none xl:max-w-none">
            Please enter your email address to receive a password reset link.
          </p>

          <form
            onSubmit={handleSubmit}
            className="w-full sm:max-lg:w-[55%] max-sm:mt-20"
          >
            <div>
              <div className="grid grid-cols-1 md:gap-1 lg:mr-12 xl:mr-36  ">
                <LoginInput
                  className=" w-full 2xl:w-full px-4 "
                  id="email"
                  name="email"
                  label="Email Address"
                  value={formData.email}
                  placeholder="Enter your email address"
                  error={touched.email && errors.email}
                  onChange={(e) =>
                    handleInputChange("email", e.target.value)
                  }
                  onBlur={() => handleBlur("email", formData.email)}
                />
              </div>
            </div>

            <div className="flex flex-col items-center justify-center mt-14 lg:mr-12 xl:mr-36 ">
              {inlineMessage && (
                <p
                  className={`mb-3 w-full text-center font-inter text-[13px] font-medium leading-5 ${
                    inlineMessage.type === "success" ? "text-green-600" : "text-light-red"
                  }`}
                >
                  {inlineMessage.text}
                </p>
              )}
              {/* Sign In Button */}
              <LoginButton
                text={
                  isLoading ? 
                  <ClipLoader
                    loading={true}
                    speedMultiplier={0.5}
                    size={22}
                    color="#60a5fa"
                  />
                  : "Request Password"
                }
                type="submit"
                disabled= {isLoading}
                className="w-full py-3 h-[54px]"
              />
              {/* Sign Up Link */}
              <p className="mt-3  font-lato text-[15px] text-center leading-[18.75px] text-black/50">
                Back to SignIn ?{" "}
                <a href="/signin" className="text-blue-shades hover:underline">
                  Sign In
                </a>
              </p>

              {/* social logins */}
              <div className="flex mt-8 w-full ">
                <div className="w-full ">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-medium-gray"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 font-inter font-medium text-level-3 leading-[24px] text-gray bg-white">
                        or
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 h-[54px] gap-4 mt-6">
                    {/* <button 
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      type="button"
                      disabled={isLoading}>
                      <img
                        src={facebookIcon}
                        alt="Facebook"
                        className="w-5 h-5"
                      /> 
                    </button> */}
                    <button
                      className="flex items-center justify-center gap-3 font-inter font-semibold text-[18px] leading-[21.6px] text-gray py-3 rounded-lg hover:bg-[#EFEFEF] transition duration-200 px-4 py-2 border rounded-md border-medium-gray "
                      onClick={handleGoogleSignin}
                      disabled={isLoading}
                    >
                      <img src={googleIcon} alt="Google" className="w-5 h-5" />
                      <span>Continue with Google</span>
                    </button>
                    {/* <button 
                      className="flex items-center justify-center px-4 py-2 border rounded-md border-medium-gray hover:bg-gray-50"
                      type="button"
                      disabled={isLoading}>
                      <img src={appleIcon} alt="Apple" className="w-5 h-5" />
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        {/* Right Side: Image */}
        <div className="hidden lg:flex items-center justify-center w-full md:max-w-[480px] lg:max-w-[580px] xl:max-w-[650px] 1920-br:-translate-x-[80px]">
          <img src={loginImage} alt="Sign Up" />
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
